import React, { useState, useEffect } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import * as mui from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import uarService from '../../services/UARService';
import { Box, Chip, Tooltip, alpha, CircularProgress, Badge, Switch, FormControlLabel } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SearchIcon from '@mui/icons-material/Search';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import InputAdornment from '@mui/material/InputAdornment';
import NoMeetingRoomIcon from '@mui/icons-material/NoMeetingRoom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import LabelIcon from '@mui/icons-material/Label';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import TuneIcon from '@mui/icons-material/Tune';
import PreviewIcon from '@mui/icons-material/Preview';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GroupsIcon from '@mui/icons-material/Groups';
import { 
  Menu, MenuItem, ListItemIcon, Divider, Card, CardContent,
  Dialog, DialogActions, DialogContent, DialogTitle, Avatar,
  Accordion, AccordionSummary, AccordionDetails, Tabs, Tab
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

// Animation keyframes
const animationStyles = `
  @keyframes pulseButton {
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4); }
    70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(25, 118, 210, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
  }
  
  @keyframes slideIn {
    from { transform: translateX(20px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }
`;

export default function UARRoleList({ 
  roles, 
  roleschosen, 
  app_name, 
  review_cycle, 
  uarfile, 
  onRolesChanged,
  appRecord = [] // This will contain the app record data with user count information
}) {
  const [availableRoles, setAvailableRoles] = useState([]);
  const [chosenRoles, setChosenRoles] = useState([]);
  const [filter, setFilter] = useState('');
  const [selectAllAvailable, setSelectAllAvailable] = useState(false);
  const [selectAllChosen, setSelectAllChosen] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});
  const [animation, setAnimation] = useState(true);
  const [roleUserCounts, setRoleUserCounts] = useState({});
  const [showOnlyWithUsers, setShowOnlyWithUsers] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [sortOrder, setSortOrder] = useState('alphabetical'); // 'alphabetical', 'userCount', 'importance'
  const [selectedTab, setSelectedTab] = useState('available');
  const [addAllRolesLoading, setAddAllRolesLoading] = useState(false); // Add loading state for add all roles button

  const [inscopeRoles, setInscopeRoles] = useState([]);
  const [availRoleCount, setAvailRoleCount] = useState(0);
  const [chosenRoleCount, setChosenRoleCount] = useState(0);

  // Add new state for user preview dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewRoleTab, setPreviewRoleTab] = useState(0); // 0 for Selected, 1 for All
  const [expandedRole, setExpandedRole] = useState(null);

  // Add global styles when component mounts
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = animationStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      // Clean up style when component unmounts
      document.head.removeChild(styleElement);
    };
  }, []);

  // Calculate user counts for each role from the appRecord
  useEffect(() => {
    if (appRecord && appRecord.length > 0) {
      // Calculate user counts for each role
      const counts = {};
      
      appRecord.forEach(record => {
        if (record.ROLE_NAME) {
          counts[record.ROLE_NAME] = (counts[record.ROLE_NAME] || 0) + 1;
        }
      });
      
      setRoleUserCounts(counts);
    }
  }, [appRecord]);

  useEffect(() => {
    // Map the chosen roles into an array of objects with a unique ID
    const inscopedRoles = roleschosen?.map((item, index) => {
        return {
          id: index,
          label: item.ROLE_NAME || "Unknown Role",  // Fallback if ROLE_NAME is missing
          ROLE_NAME: item.ROLE_NAME,
          roleID: item.roleID,
          userCount: roleUserCounts[item.ROLE_NAME] || 0
        };
      });

    const role = roles
      .filter(item => !roleschosen.some(chosen => chosen.ROLE_NAME === item)) // Normalize case for comparison
      .map((item, index) => {
        return {
          id: index,
          label: item,
          APP_NAME: app_name,
          ROLE_NAME: item,
          REVIEW_CYCLE: review_cycle,
          UAR_FILE: uarfile,
          userCount: roleUserCounts[item] || 0
        };
      });

    setChosenRoles(inscopedRoles);
    setAvailableRoles(role); 
  }, [roles, roleschosen, app_name, review_cycle, uarfile, roleUserCounts]);
  
  const handleAddAllRolesWithUsers = async () => {
    if (availableRoles.length === 0) return;
    
    // Set button loading state
    setAddAllRolesLoading(true);
    
    // Filter roles that have users
    const rolesWithUsers = availableRoles.filter(role => roleUserCounts[role.label] && roleUserCounts[role.label] > 0);
    
    if (rolesWithUsers.length === 0) {
      // Show alert or notification that no roles have users
      setAddAllRolesLoading(false);
      return;
    }
    
    // Show loading state for roles with users
    const loadingState = {};
    rolesWithUsers.forEach(role => {
      loadingState[role.id] = true;
    });
    setLoadingStates(prev => ({ ...prev, ...loadingState }));
    
    try {
      // Process in batches to avoid overwhelming the server
      const batchSize = 5;
      const batches = [];
      
      for (let i = 0; i < rolesWithUsers.length; i += batchSize) {
        batches.push(rolesWithUsers.slice(i, i + batchSize));
      }
      
      for (const batch of batches) {
        const promises = batch.map(role => {
          const inscopeData = {
            APP_NAME: app_name,
            REVIEW_CYCLE: review_cycle,
            UAR_FILE: uarfile,
            ROLE_NAMES: role.label,
            roleID: role.roleID
          };
          return uarService.createInscopeRoles(inscopeData);
        });
        
        await Promise.all(promises);
      }
      
      // After all API calls succeed, update the UI
      const updatedAvailableRoles = availableRoles.filter(
        role => !rolesWithUsers.some(r => r.id === role.id)
      );
      const updatedChosenRoles = [...chosenRoles, ...rolesWithUsers];
      
      setAvailableRoles(updatedAvailableRoles);
      setChosenRoles(updatedChosenRoles);
      
      // Notify parent component
      onRolesChanged(updatedChosenRoles); 
      
      // Reset animation to trigger it again
      setAnimation(false);
      setTimeout(() => setAnimation(true), 10);
    } catch (error) {
      console.error('Error tagging roles with users', error);
    
      // Clear all loading states
      const clearedLoadingState = {};
      rolesWithUsers.forEach(role => {
        clearedLoadingState[role.id] = false;
      });
      setLoadingStates(prev => ({ ...prev, ...clearedLoadingState }));
    } finally {
      // Clear button loading state
      setAddAllRolesLoading(false);
    }
  };

  const handleFilterMenuClick = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  const handleSortOrderChange = (order) => {
    setSortOrder(order);
    handleFilterMenuClose();
  };
  
  const toggleShowOnlyWithUsers = () => {
    setShowOnlyWithUsers(!showOnlyWithUsers);
  };

  // Handle moving a role between lists
  const handleToggle = async (role) => {
    try {
      // Set loading state for this specific role
      setLoadingStates(prev => ({ ...prev, [role.label]: true }));
      
      // Copy state to avoid direct mutations
      let newAvailableRoles = [...availableRoles];
      let newChosenRoles = [...chosenRoles];
      
      try {
        // Call API to create in-scope role (corrected format for ROLE_NAMES)
        const roleData = {
          ROLE_NAMES: role.label, // Send as string, not array
          UAR_FILE: uarfile,
          REVIEW_CYCLE: review_cycle,
          APP_ID: app_name
        };
        
        const response = await uarService.createInscopeRoles(roleData);
        
        if (response && response.id) {
          // Create the role object to be added to chosenRoles
          const newRoleObj = {
            id: chosenRoles.length,
            roleID: response.id,  // Store the ID returned from API
            ROLE_NAME: role.label,
          };
          
          // Only update UI after successful API call
          newAvailableRoles = newAvailableRoles.filter(r => r.id !== role.id);
          newChosenRoles = [...newChosenRoles, newRoleObj];
          
          // Update state with new lists
          setAvailableRoles(newAvailableRoles);
          setChosenRoles(newChosenRoles);
          
          // Set tab to selected if it's empty before this addition
          if (selectedTab === 'available' && newChosenRoles.length === 1) {
            setSelectedTab('selected');
          }
          
          // Notify parent component that roles have changed
          if (onRolesChanged) {
            onRolesChanged(newChosenRoles);
          }
        }
    } catch (error) {
        console.error('Error adding role:', error);
      }
    } finally {
      // Clear the loading state for this role
      setLoadingStates(prev => ({ ...prev, [role.label]: false }));
    }
  };
  
  const handleRemove = async (roleObj) => {
    try {
      // Set loading state for this specific role
      setLoadingStates(prev => ({ ...prev, [roleObj.ROLE_NAME]: true }));
      
      // Copy state to avoid direct mutations
      let newAvailableRoles = [...availableRoles];
      let newChosenRoles = [...chosenRoles];
      
      try {
        // Call API to delete in-scope role
        await uarService.deleteInscopeRoles(roleObj.roleID);
        
        // Create a new available role object with the correct structure
        const roleToAdd = {
          id: newAvailableRoles.length,
          label: roleObj.ROLE_NAME,
          APP_NAME: app_name,
          ROLE_NAME: roleObj.ROLE_NAME,
          REVIEW_CYCLE: review_cycle,
          UAR_FILE: uarfile,
          userCount: roleUserCounts[roleObj.ROLE_NAME] || 0
        };
        
        // Add the role back to available roles if it doesn't exist
        if (!newAvailableRoles.some(r => r.label === roleObj.ROLE_NAME)) {
          newAvailableRoles = [...newAvailableRoles, roleToAdd];
        }
        
        // Only remove the specific role from chosen roles - keep this AFTER adding to available
        newChosenRoles = newChosenRoles.filter(r => r.roleID !== roleObj.roleID);
        
        // Update state with new lists - update both simultaneously
        setAvailableRoles(newAvailableRoles);
        setChosenRoles(newChosenRoles);
        
        // If removing the last item from "selected" tab, switch to "available" tab
        if (selectedTab === 'selected' && newChosenRoles.length === 0) {
          setSelectedTab('available');
        }
        
        // Notify parent component that roles have changed
        if (onRolesChanged) {
          onRolesChanged(newChosenRoles);
        }
      } catch (error) {
        console.error('Error removing role:', error);
      }
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({ ...prev, [roleObj.ROLE_NAME]: false }));
    }
  };
  
  const handleSelectAllAvailable = async () => {
    if (filteredAvailableRoles.length === 0) return;
    
    // Create a loading state object for all roles that will be processed
    const loadingState = {};
    filteredAvailableRoles.forEach(role => {
      loadingState[role.label] = true;
    });
    setLoadingStates(prev => ({ ...prev, ...loadingState }));
    
    let newAvailableRoles = [...availableRoles];
    let newChosenRoles = [...chosenRoles];
    
    // Process roles in batches to avoid overwhelming the server
    const batchSize = 5;
    const batches = Math.ceil(filteredAvailableRoles.length / batchSize);
    
    try {
      for (let i = 0; i < batches; i++) {
        const start = i * batchSize;
        const end = Math.min(start + batchSize, filteredAvailableRoles.length);
        const batch = filteredAvailableRoles.slice(start, end);
        
        // Process each role in the current batch
        const promises = batch.map(async (role) => {
          try {
            const roleData = {
              ROLE_NAMES: role.label, // Send as string, not array
              UAR_FILE: uarfile,
              REVIEW_CYCLE: review_cycle,
              APP_ID: app_name
            };
            
            const response = await uarService.createInscopeRoles(roleData);
            
            if (response && response.id) {
              // Create role object for chosen roles
              return {
                id: newChosenRoles.length + batch.indexOf(role),
                roleID: response.id,
                ROLE_NAME: role.label
              };
            }
            return null;
    } catch (error) {
            console.error(`Error adding role ${role.label}:`, error);
            return null;
          }
        });
        
        // Wait for all promises in this batch to resolve
        const results = await Promise.all(promises);
        const successfulResults = results.filter(Boolean);
        
        // Update state with successful results
        if (successfulResults.length > 0) {
          // Update available and chosen roles arrays
          const addedRoleIds = batch
            .filter((_, index) => results[index] !== null)
            .map(r => r.id);
          
          newAvailableRoles = newAvailableRoles.filter(r => !addedRoleIds.includes(r.id));
          newChosenRoles = [...newChosenRoles, ...successfulResults];
        }
      }
      
      // Update state after all batches are processed
      setAvailableRoles(newAvailableRoles);
      setChosenRoles(newChosenRoles);
      
      // Switch to selected tab after adding all roles
      if (newChosenRoles.length > 0) {
        setSelectedTab('selected');
      }
      
      // Notify parent component
      if (onRolesChanged) {
        onRolesChanged(newChosenRoles);
      }
    } catch (error) {
      console.error('Error selecting all roles:', error);
    } finally {
      // Clear all loading states
      const clearedLoadingState = {};
      filteredAvailableRoles.forEach(role => {
        clearedLoadingState[role.label] = false;
      });
      setLoadingStates(prev => ({ ...prev, ...clearedLoadingState }));
    }
  };

  const handleDeselectAllChosen = async () => {
    if (filteredChosenRoles.length === 0) return;
    
    // Create a loading state object for all roles that will be processed
    const loadingState = {};
    filteredChosenRoles.forEach(role => {
      loadingState[role.ROLE_NAME] = true;
    });
    setLoadingStates(prev => ({ ...prev, ...loadingState }));
    
    let newAvailableRoles = [...availableRoles];
    let newChosenRoles = [...chosenRoles];
    let rolesToRemove = [];
    
    // Process roles in batches to avoid overwhelming the server
    const batchSize = 5;
    const batches = Math.ceil(filteredChosenRoles.length / batchSize);
    
    try {
      // First collect all roles to move back
      for (let i = 0; i < batches; i++) {
        const start = i * batchSize;
        const end = Math.min(start + batchSize, filteredChosenRoles.length);
        const batch = filteredChosenRoles.slice(start, end);
        
        // Process each role in the current batch
        const promises = batch.map(async (role) => {
          try {
            await uarService.deleteInscopeRoles(role.roleID);
            return { 
              success: true, 
              roleName: role.ROLE_NAME, 
              roleID: role.roleID 
            };
          } catch (error) {
            console.error(`Error removing role ${role.ROLE_NAME}:`, error);
            return { 
              success: false, 
              roleName: role.ROLE_NAME 
            };
          }
        });
        
        // Wait for all promises in this batch to resolve
        const results = await Promise.all(promises);
        
        // Process successful results
        results.forEach(result => {
          if (result.success) {
            rolesToRemove.push(result.roleID);
            
            // Create role object to add back to available
            const roleToAdd = {
              id: newAvailableRoles.length + rolesToRemove.length,
              label: result.roleName,
              APP_NAME: app_name,
              ROLE_NAME: result.roleName,
              REVIEW_CYCLE: review_cycle,
              UAR_FILE: uarfile,
              userCount: roleUserCounts[result.roleName] || 0
            };
            
            // Add to available roles if it doesn't already exist
            if (!newAvailableRoles.some(r => r.label === result.roleName)) {
              newAvailableRoles.push(roleToAdd);
            }
          }
        });
      }
      
      // Now update the chosen roles list - do this after adding all to available
      if (rolesToRemove.length > 0) {
        newChosenRoles = newChosenRoles.filter(r => !rolesToRemove.includes(r.roleID));
      }
      
      // Update both state variables simultaneously
      setAvailableRoles(newAvailableRoles);
      setChosenRoles(newChosenRoles);
      
      // Switch to available tab if all chosen roles were removed
      if (newChosenRoles.length === 0) {
        setSelectedTab('available');
      }
      
      // Notify parent component
      if (onRolesChanged) {
        onRolesChanged(newChosenRoles);
      }
    } catch (error) {
      console.error('Error deselecting all roles:', error);
    } finally {
      // Clear all loading states
      const clearedLoadingState = {};
      filteredChosenRoles.forEach(role => {
        clearedLoadingState[role.ROLE_NAME] = false;
      });
      setLoadingStates(prev => ({ ...prev, ...clearedLoadingState }));
    }
  };

  const handleResetSelectAll = () => {
    setSelectAllAvailable(false);
    setSelectAllChosen(false);
  };

  // Filter Available Roles by label
  const getFilteredAndSortedAvailableRoles = () => {
    // First filter by search query
    let filtered = availableRoles.filter((item) =>
      item && item.label && item.label.toLowerCase().includes(filter.toLowerCase())
    );
    
    // Then filter by user count if needed
    if (showOnlyWithUsers) {
      filtered = filtered.filter(item => item.userCount > 0);
    }
    
    // Finally sort according to current sort order
    return filtered.sort((a, b) => {
      if (sortOrder === 'alphabetical') {
        return (a.label || '').localeCompare(b.label || '');
      } else if (sortOrder === 'userCount') {
        return (b.userCount || 0) - (a.userCount || 0);
      } else if (sortOrder === 'importance') {
        // Importance is based on user count, but puts roles with at least one user at the top
        const aHasUsers = (a.userCount || 0) > 0;
        const bHasUsers = (b.userCount || 0) > 0;
        if (aHasUsers !== bHasUsers) {
          return aHasUsers ? -1 : 1;
        }
        return (b.userCount || 0) - (a.userCount || 0);
      }
      return 0;
    });
  };

  // Filter Chosen Roles by ROLE_NAME with same sorting logic
  const getFilteredAndSortedChosenRoles = () => {
    let filtered = chosenRoles.filter((item) =>
      item && item.ROLE_NAME && item.ROLE_NAME.toLowerCase().includes(filter.toLowerCase())
    );
    
    return filtered.sort((a, b) => {
      if (sortOrder === 'alphabetical') {
        return ((a && a.ROLE_NAME) || '').localeCompare((b && b.ROLE_NAME) || '');
      } else if (sortOrder === 'userCount') {
        return (b && b.userCount || roleUserCounts[b && b.ROLE_NAME] || 0) - 
               (a && a.userCount || roleUserCounts[a && a.ROLE_NAME] || 0);
      } else if (sortOrder === 'importance') {
        const aHasUsers = (a && a.userCount || roleUserCounts[a && a.ROLE_NAME] || 0) > 0;
        const bHasUsers = (b && b.userCount || roleUserCounts[b && b.ROLE_NAME] || 0) > 0;
        if (aHasUsers !== bHasUsers) {
          return aHasUsers ? -1 : 1;
        }
        return (b && b.userCount || roleUserCounts[b && b.ROLE_NAME] || 0) - 
               (a && a.userCount || roleUserCounts[a && a.ROLE_NAME] || 0);
      }
      return 0;
    });
  };
  
  const filteredAvailableRoles = getFilteredAndSortedAvailableRoles();
  const filteredChosenRoles = getFilteredAndSortedChosenRoles();
  
  // Calculate stats for the UI
  const totalRoles = roles.length;
  const rolesWithUsers = roles.filter(role => roleUserCounts[role] && roleUserCounts[role] > 0).length;
  const selectedRolesWithUsers = chosenRoles.filter(role => 
    role.userCount > 0 || 
    (roleUserCounts[role.ROLE_NAME] && roleUserCounts[role.ROLE_NAME] > 0)
  ).length;
  
  // Calculate if we have unselected roles with users
  const hasUnselectedRolesWithUsers = availableRoles.some(role => 
    role.userCount > 0 || 
    (roleUserCounts[role.label] && roleUserCounts[role.label] > 0)
  );

  // Function to handle opening the preview dialog
  const handleOpenPreviewDialog = () => {
    setPreviewDialogOpen(true);
  };
  
  // Function to handle closing the preview dialog
  const handleClosePreviewDialog = () => {
    setPreviewDialogOpen(false);
    setExpandedRole(null);
  };
  
  // Function to toggle expanded role
  const handleToggleExpandRole = (roleId) => {
    if (expandedRole === roleId) {
      setExpandedRole(null);
    } else {
      setExpandedRole(roleId);
    }
  };
  
  // Function to handle tab change in the preview dialog
  const handlePreviewTabChange = (event, newValue) => {
    setPreviewRoleTab(newValue);
  };
  
  // Prepare data for preview - group users by role
  const getUsersByRole = () => {
    const usersByRole = {};
    
    // Only process if appRecord exists
    if (!appRecord || appRecord.length === 0) {
      return usersByRole;
    }
    
    // Collect users for each role
    appRecord.forEach(record => {
      if (!record.ROLE_NAME) return;
      
      if (!usersByRole[record.ROLE_NAME]) {
        usersByRole[record.ROLE_NAME] = [];
      }
      
      // Add user info (avoid duplicates)
      const existingUser = usersByRole[record.ROLE_NAME].find(u => u.id === record.USER_ID);
      if (!existingUser) {
        // Determine the best name to display
        let displayName = 'Unknown User';
        
        if (record.USER_NAME) {
          displayName = record.USER_NAME;
        } else if (record.FIRST_NAME || record.LAST_NAME) {
          displayName = `${record.FIRST_NAME || ''} ${record.LAST_NAME || ''}`.trim();
        } else if (record.EMAIL_ADDRESS) {
          displayName = record.EMAIL_ADDRESS;
        }
        
        usersByRole[record.ROLE_NAME].push({
          id: record.USER_ID,
          name: displayName,
          email: record.EMAIL_ADDRESS || '',
          active: record.IS_ACTIVE || true,
          department: record.DEPARTMENT || '',
          manager: record.MANAGER_NAME || '',
        });
      }
    });
    
    return usersByRole;
  };
  
  // Get sorted list of roles for preview
  const getPreviewRoles = () => {
    if (previewRoleTab === 0) {
      // Show only chosen roles
      return chosenRoles.map(r => r.ROLE_NAME).sort();
    } else {
      // Show all roles that have users
      return Object.keys(getUsersByRole()).sort();
    }
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      maxHeight: '80vh', // Set maximum height to ensure modal content is scrollable
    }}>
      {/* Instructions Panel - Extremely compact with inline display */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        backgroundColor: alpha('#edf7fe', 0.9),
        border: '1px solid',
        borderColor: alpha('#1976d2', 0.2),
        borderRadius: '8px',
        py: 1,
        px: 1.5,
        mx: 2,
        mb: 1.5,
        gap: 1.5,
      }}>
        <InfoIcon color="primary" sx={{ fontSize: 18, flexShrink: 0 }} />
        <Box>
          <Typography variant="caption" color="primary.dark" sx={{ fontWeight: 600, display: 'block', mb: 0.3 }}>
            Selection Instructions
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Select roles for compliance review. Only selected roles will be included.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: alpha('#2196f3', 0.9) }}></Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Blue = has users</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: alpha('#4caf50', 0.9) }}></Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Green = selected</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Stats and Actions Bar */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 2, 
          mx: 2,
          borderRadius: '12px',
          bgcolor: alpha('#f8f9fa', 0.8),
          border: '1px solid',
          borderColor: 'divider',
          p: 1,
          minHeight: '70px', // Reduced height
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap' }}>
            <Box sx={{ 
              textAlign: 'center',
              minWidth: '100px',
              flex: '0 0 auto'
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3 }}>
                Total Roles
              </Typography>
              <Chip 
                label={totalRoles} 
                size="small" 
                color="primary" 
                variant="outlined"
                icon={<LabelIcon fontSize="small" />}
              />
            </Box>
            
            <Box sx={{ 
              textAlign: 'center',
              minWidth: '120px',
              flex: '0 0 auto'
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3 }}>
                Roles with Users
              </Typography>
              <Chip 
                label={rolesWithUsers} 
                size="small" 
                color="info" 
                variant="outlined"
                icon={<PeopleAltIcon fontSize="small" />}
              />
            </Box>
            
            <Box sx={{ 
              textAlign: 'center',
              minWidth: '150px',
              flex: '0 0 auto'
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3 }}>
                Selected Roles with Users
              </Typography>
              <Chip 
                label={`${selectedRolesWithUsers}/${rolesWithUsers}`} 
                size="small" 
                color="success" 
                variant={selectedRolesWithUsers === rolesWithUsers ? "filled" : "outlined"}
                icon={<CheckIcon fontSize="small" />}
              />
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Preview Users Button */}
            <Button
              variant="outlined"
              color="info"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={handleOpenPreviewDialog}
              disabled={appRecord.length === 0}
              data-preview-users-button="true"
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                py: 0.5
              }}
            >
              Preview Users
            </Button>
          
            {/* Add All Roles with Users Button */}
            {hasUnselectedRolesWithUsers && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={addAllRolesLoading ? null : <PlaylistAddCheckIcon />}
                onClick={handleAddAllRolesWithUsers}
                disabled={addAllRolesLoading}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  borderRadius: '8px',
                  animation: !addAllRolesLoading ? 'bounce 2s infinite' : 'none',
                  whiteSpace: 'nowrap',
                  py: 0.5,
                  position: 'relative'
                }}
              >
                {addAllRolesLoading ? (
                  <>
                    <CircularProgress
                      size={16}
                      sx={{
                        color: 'inherit',
                        position: 'absolute',
                        left: '10px',
                      }}
                    />
                    Adding Roles...
                  </>
                ) : (
                  'Add All Roles with Users'
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Card>

      {/* Search and Filter Controls - Simplified with Button Inline */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mx: 2 }}>
        <TextField
          placeholder="Search roles"
          variant="outlined"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          fullWidth
          size="small"
          sx={{ 
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              height: '36px'
            }
          }}
          InputProps={{
            style: { fontSize: '14px' },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        
        <IconButton 
          size="small"
          aria-label="filter and sort options"
          onClick={handleFilterMenuClick}
          sx={{ 
            bgcolor: alpha('#1976d2', 0.08),
            borderRadius: '8px',
            p: 0.8,
            height: '36px',
            width: '36px',
            '&:hover': { 
              bgcolor: alpha('#1976d2', 0.15)
            }
          }}
        >
          <TuneIcon fontSize="small" sx={{ color: '#1976d2' }} />
        </IconButton>
        
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={handleFilterMenuClose}
          elevation={2}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{ mt: 1 }}
        >
          <MenuItem dense>
            <FormControlLabel
              control={
                <Switch 
                  size="small"
                  checked={showOnlyWithUsers}
                  onChange={toggleShowOnlyWithUsers}
                  color="primary"
                />
              }
              label="Show only roles with users"
            />
          </MenuItem>
          
          <Divider textAlign="left" sx={{ my: 0.5 }}>Sort by</Divider>
          
          <MenuItem onClick={() => handleSortOrderChange('alphabetical')} dense>
            <ListItemIcon>
              {sortOrder === 'alphabetical' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            Alphabetical
          </MenuItem>
          
          <MenuItem onClick={() => handleSortOrderChange('userCount')} dense>
            <ListItemIcon>
              {sortOrder === 'userCount' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            User Count
          </MenuItem>
          
          <MenuItem onClick={() => handleSortOrderChange('importance')} dense>
            <ListItemIcon>
              {sortOrder === 'importance' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            Importance
          </MenuItem>
        </Menu>
      </Box>

      {/* Tabs - Compact design */}
      <Box sx={{ display: 'flex', mx: 2, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Button
          onClick={() => setSelectedTab('available')}
          sx={{
            textTransform: 'none',
            fontWeight: selectedTab === 'available' ? 600 : 400,
            color: selectedTab === 'available' ? 'primary.main' : 'text.secondary',
            borderBottom: selectedTab === 'available' ? 2 : 0,
            borderColor: 'primary.main',
            borderRadius: 0,
            px: 2,
            py: 0.5, // Reduced padding
            minHeight: '36px' // Compact height
          }}
        >
          Available Roles ({filteredAvailableRoles.length})
        </Button>
        <Button
          onClick={() => setSelectedTab('selected')}
          sx={{
            textTransform: 'none',
            fontWeight: selectedTab === 'selected' ? 600 : 400,
            color: selectedTab === 'selected' ? 'success.main' : 'text.secondary',
            borderBottom: selectedTab === 'selected' ? 2 : 0,
            borderColor: 'success.main',
            borderRadius: 0,
            px: 2,
            py: 0.5, // Reduced padding
            minHeight: '36px' // Compact height
          }}
        >
          Selected Roles ({filteredChosenRoles.length})
        </Button>
      </Box>

      {/* Container for both lists - Fixed height */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        gap: '20px', 
        px: 2,
        flex: 1,
        overflow: 'hidden',
        minHeight: '450px' // Increased minimum height for role lists
      }}>
          {/* Available Roles List */}
        <Box sx={{ 
          flex: 1, 
          display: selectedTab === 'available' ? 'flex' : 'none',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '10px',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.03)',
          height: '100%' // Use full height of container
        }}>
          <Box sx={{ 
            p: 1.5, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            bgcolor: 'background.default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Available Roles
            </Typography>
            <Chip 
              label={`${filteredAvailableRoles.length} roles`} 
              size="small" 
              color="primary"
              variant="outlined"
            />
          </Box>
          
          {filteredAvailableRoles.length > 0 && (
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon={<PlaylistAddCheckIcon fontSize="small" />}
              onClick={handleSelectAllAvailable}
              sx={{ 
                mx: 1, 
                my: 0.5,
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                height: '28px'
              }}
            >
              Add all roles
            </Button>
          )}
          
          <Box sx={{ 
            overflowY: 'auto', // Make sure this scrolls
            px: 1, 
            py: 0.5, 
            pb: 2, // Add extra padding at the bottom to ensure last item is visible
            flex: 1,
            height: 'calc(100% - 90px)' // Adjusted height calculation to allow more space for content
          }}>
            {filteredAvailableRoles.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 4,
                opacity: 0.7
              }}>
                <NoMeetingRoomIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No available roles found
                </Typography>
              </Box>
            ) : (
              <List sx={{ width: '100%', pb: 2 }}> {/* Add bottom padding to the list */}
              {filteredAvailableRoles.map((value) => (
                <ListItem
                  key={value.id}
                    disablePadding
                    sx={{ 
                      mb: 0.5,
                      animation: animation ? 'fadeIn 0.3s ease-out' : 'none',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha('#1976d2', 0.04),
                        transform: 'translateX(2px)'
                      },
                      bgcolor: value.userCount > 0 ? alpha('#2196f3', 0.04) : 'transparent',
                      borderLeft: value.userCount > 0 ? '3px solid' : 'none',
                      borderColor: value.userCount > 0 ? alpha('#2196f3', 0.4) : 'transparent',
                    }}
                  >
                    <ListItemButton
                      sx={{ 
                        borderRadius: '8px',
                        py: 0.5,
                        transition: 'all 0.2s',
                      }}
                      onClick={() => handleToggle(value)}
                    >
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              variant="body2" 
                              noWrap 
                              title={value.label}
                              sx={{ 
                                fontWeight: 500,
                                mr: 1.5, // Increased spacing between text and badge
                                flexShrink: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {value.label}
                            </Typography>
                            {value.userCount > 0 && (
                              <Chip
                                size="small"
                                label={value.userCount}
                                color="info"
                                variant="outlined"
                                icon={<PeopleAltIcon fontSize="small" />}
                                sx={{ 
                                  ml: 1, 
                                  height: 20, 
                                  minWidth: '32px',
                                  '& .MuiChip-label': { px: 0.5 },
                                  flexShrink: 0 // Prevent chip from shrinking
                                }}
                              />
                            )}
                          </Box>
                        } 
                      />
                      {loadingStates[value.label] ? (
                        <CircularProgress size={24} color="primary" thickness={4} />
                      ) : (
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            bgcolor: alpha('#1976d2', 0.08),
                            color: '#1976d2',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: alpha('#1976d2', 0.15),
                              transform: 'scale(1.1)',
                              animation: 'pulseButton 1s infinite'
                            }
                          }}
                        >
                          <AddCircleIcon fontSize="small" />
                        </Box>
                      )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            )}
          </Box>
        </Box>

          {/* Chosen Roles List */}
        <Box sx={{ 
          flex: 1, 
          display: selectedTab === 'selected' ? 'flex' : 'none',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '10px',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.03)',
          height: '100%' // Use full height of container
        }}>
          <Box sx={{ 
            p: 1.5, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            bgcolor: alpha('#4caf50', 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckIcon sx={{ mr: 1, color: '#4caf50', fontSize: 18 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Selected Roles
              </Typography>
            </Box>
            <Chip 
              label={`${filteredChosenRoles.length} selected`} 
              size="small" 
              color="success"
              variant={filteredChosenRoles.length > 0 ? "filled" : "outlined"}
            />
          </Box>
          
          {filteredChosenRoles.length > 0 && (
            <Button
              variant="text"
              color="error"
              size="small"
              startIcon={<RemoveCircleOutlineIcon fontSize="small" />}
              onClick={handleDeselectAllChosen}
              sx={{ 
                mx: 1, 
                my: 0.5,
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                height: '28px'
              }}
            >
              Remove all
            </Button>
          )}
          
          <Box sx={{ 
            overflowY: 'auto', // Make sure this scrolls
            px: 1, 
            py: 0.5, 
            pb: 2, // Add extra padding at the bottom to ensure last item is visible
            flex: 1,
            height: 'calc(100% - 90px)' // Adjusted height calculation to allow more space for content
          }}>
            {filteredChosenRoles.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 4,
                opacity: 0.7
              }}>
                <AssignmentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No roles selected for review
                </Typography>
              </Box>
            ) : (
              <List sx={{ width: '100%', pb: 2 }}> {/* Add bottom padding to the list */}
                {filteredChosenRoles.map((value, index) => (
                <ListItem
                    key={index}
                  secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          bgcolor: alpha('#f44336', 0.08),
                          borderRadius: '50%',
                          p: 0.5
                        }}>
                          {loadingStates[value.ROLE_NAME] ? (
                            <CircularProgress size={24} color="error" thickness={4} />
                          ) : (
                    <IconButton
                      edge="end"
                      aria-label="remove"
                              onClick={() => handleRemove(value)}
                              size="small"
                              sx={{
                                color: '#f44336',
                                '&:hover': { bgcolor: alpha('#f44336', 0.15) }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                    </IconButton>
                          )}
                        </Box>
                      </Box>
                  }
                  disablePadding
                >
                    <ListItemButton
                      role={undefined}
                      dense
                      sx={{
                        pl: 2,
                        pr: 8,
                        py: 1,
                        borderLeft: '3px solid',
                        borderColor: roleUserCounts[value.ROLE_NAME] ? alpha('#4caf50', 0.8) : alpha('#4caf50', 0.4),
                        bgcolor: alpha('#4caf50', 0.04),
                        mb: 0.5,
                        borderRadius: '4px',
                        '&:hover': {
                          bgcolor: alpha('#4caf50', 0.08),
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              component="span" 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                mr: 1.5, // Increased margin for better spacing
                                flexShrink: 1, // Allow truncation if needed
                                maxWidth: roleUserCounts[value.ROLE_NAME] > 0 ? 'calc(100% - 40px)' : '100%' // Reserve space for badge
                              }}
                            >
                              {value.ROLE_NAME}
                            </Typography>
                            {roleUserCounts[value.ROLE_NAME] > 0 && (
                              <Badge 
                                badgeContent={roleUserCounts[value.ROLE_NAME]} 
                                color="primary"
                                sx={{ 
                                  '& .MuiBadge-badge': { 
                                    fontSize: '0.65rem', 
                                    height: '18px', 
                                    minWidth: '18px',
                                    padding: '0 6px'
                                  },
                                  flexShrink: 0 // Prevent badge from shrinking
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            )}
          </Box>
        </Box>
      </Box>

      {/* User Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreviewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          borderBottom: '1px solid', 
          borderColor: 'divider', 
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <GroupsIcon color="primary" sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            User List Preview
          </Typography>
        </DialogTitle>
        
        <Box sx={{ px: 3, pt: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={previewRoleTab}
            onChange={handlePreviewTabChange}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
            sx={{ 
              mb: 1,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: '48px'
              }
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon fontSize="small" />
                  <Typography variant="body2">Selected Roles Only</Typography>
                  <Chip 
                    label={chosenRoles.length} 
                    size="small" 
                    color="primary" 
                    sx={{ height: 20, ml: 0.5, fontWeight: 'bold' }} 
                  />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleAltIcon fontSize="small" />
                  <Typography variant="body2">All Roles with Users</Typography>
                  <Chip 
                    label={Object.keys(getUsersByRole()).length} 
                    size="small" 
                    color="info" 
                    sx={{ height: 20, ml: 0.5, fontWeight: 'bold' }} 
                  />
                </Box>
              } 
            />
          </Tabs>
        </Box>
        
        <DialogContent sx={{ px: 2, pb: 1 }}>
          {appRecord.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              py: 5,
              textAlign: 'center'
            }}>
              <ErrorOutlineIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No User Data Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The application record doesn't contain user information.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box sx={{ mb: 2, px: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {getPreviewRoles().length} roles with a total of {appRecord.length} user assignments.
                  Click on a role to see the assigned users.
                </Typography>
              </Box>
              
              {getPreviewRoles().length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 4,
                  textAlign: 'center'
                }}>
                  <HelpOutlineIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    No roles available to display
                  </Typography>
                  {previewRoleTab === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Select some roles first
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ overflowY: 'auto', maxHeight: '50vh', px: 1 }}>
                  {getPreviewRoles().map((roleName, idx) => {
                    const userList = getUsersByRole()[roleName] || [];
                    const isExpanded = expandedRole === roleName;
                    
                    return (
                      <Card 
                        key={idx} 
                        elevation={0} 
                        sx={{ 
                          mb: 1, 
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          onClick={() => handleToggleExpandRole(roleName)}
                          sx={{ 
                            px: 2, 
                            py: 1.5, 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: isExpanded ? alpha('#2196f3', 0.08) : 'background.paper',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: isExpanded ? alpha('#2196f3', 0.12) : alpha('#2196f3', 0.04)
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ 
                              width: 28, 
                              height: 28, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              borderRadius: '50%',
                              bgcolor: alpha('#2196f3', 0.12),
                              color: 'primary.main',
                              mr: 1.5
                            }}>
                              <LabelIcon sx={{ fontSize: 16 }} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {roleName}
                            </Typography>
                            <Chip
                              label={userList.length}
                              size="small"
                              color="primary"
                              sx={{ ml: 2, height: 24 }}
                              icon={<PersonIcon sx={{ fontSize: '0.8rem !important' }} />}
                            />
                            {chosenRoles.some(r => r.ROLE_NAME === roleName) && (
                              <Chip
                                label="Selected"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ ml: 1, height: 24 }}
                                icon={<CheckIcon sx={{ fontSize: '0.8rem !important' }} />}
                              />
                            )}
                          </Box>
                          <Box>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </Box>
                        </Box>
                        
                        {isExpanded && (
                          <Box sx={{ 
                            px: 2, 
                            py: 1.5, 
                            bgcolor: alpha('#f5f5f5', 0.5),
                            borderTop: '1px solid',
                            borderColor: 'divider'
                          }}>
                            {userList.length === 0 ? (
                              <Box sx={{ 
                                py: 2, 
                                textAlign: 'center',
                                color: 'text.secondary'
                              }}>
                                <Typography variant="body2">
                                  No users assigned to this role
                                </Typography>
                              </Box>
                            ) : (
                              <List disablePadding sx={{ maxHeight: '300px', overflow: 'auto' }}>
                                {userList.map((user, userIdx) => (
                                  <ListItem
                                    key={userIdx}
                                    disablePadding
                                    sx={{ 
                                      py: 0.75, 
                                      borderBottom: userIdx < userList.length - 1 ? '1px solid' : 'none',
                                      borderColor: alpha('#000', 0.05)
                                    }}
                                  >
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      width: '100%'
                                    }}>
                                      <Avatar 
                                        alt={user.name} 
                                        src="/static/images/avatar/1.jpg" 
                                        sx={{ 
                                          width: 32, 
                                          height: 32, 
                                          mr: 1.5,
                                          bgcolor: theme => 
                                            theme.palette.primary.main
                                        }}
                                      >
                                        {user.name.charAt(0)}
                                      </Avatar>
                                      
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {user.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {user.email || 'No email provided'}
                                        </Typography>
                                      </Box>
                                      
                                      {user.department && (
                                        <Chip
                                          label={user.department}
                                          size="small"
                                          variant="outlined"
                                          color="default"
                                          sx={{ 
                                            height: 20, 
                                            fontSize: '0.7rem',
                                            mr: 0.5
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </Box>
                        )}
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={handleClosePreviewDialog} 
            variant="outlined"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
